import { Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { signOut } from 'firebase/auth';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { FC, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { auth } from '@/config';
import { useHeroToast, warmupFunction } from '@/hooks';
import { getBrowserLanguage, PANEL_ACCESS_DENIED_KEY, PanelAccessError, useLanguageState, useUserState } from '@/state';
import { LocaleLink, LocaleNavigate, useLocaleNavigate } from '@/utils/locale';

import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
/* Oficjalne znaki marek ibis w wersji z 2019 roku: czerwony ibis, zielony ibis Styles,
   niebieski ibis budget. SVG, więc są ostre w każdej skali i ważą razem 8 kB. `?url`
   jest konieczne, bo svgr domyślnie zwraca komponent, a tu potrzebny jest adres pliku. */
import logoIbis from '../../../assets/logos/ibis.svg?url';
import logoIbisBudget from '../../../assets/logos/ibis-budget.svg?url';
import logoIbisStyles from '../../../assets/logos/ibis-styles.svg?url';
import logoAccor from '../../../assets/logos/Logo_Accor.png';
import logoEssendi from '../../../assets/logos/Logo_ESSENDI_avec_Sign_RVB_VertF-e1751197610724.png';
import logoForestPark from '../../../assets/logos/logo-forestpark-dark.png';
import logoMercure from '../../../assets/logos/Mercure_Hotels_Logo_2013.svg.png';
import logoNickel from '../../../assets/logos/nickel grzybowo logo.png';
import logoNovotel from '../../../assets/logos/Novotel_logo_(2016).svg.png';
import { i18n } from '../../../utils/i18n';
import { MarketingSlideshow } from '../components/MarketingSlideshow';
import {
  AuthContainer,
  AuthForm,
  CheckboxWrapper,
  Divider,
  ForgotPasswordLink,
  FormContainer,
  FormWrapper,
  InputGroup,
  InputIcon,
  InputLabel,
  InputWrapper,
  LegalDisclaimer,
  LogoWrapper,
  MarketingContent,
  MarketingSection,
  MarketingTitle,
  PartnersGrid,
  PartnersSection,
  PartnersTitle,
  PasswordToggle,
  RememberRow,
  SocialButton,
  StyledInput,
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ErrorMessage = styled.p`
  margin-top: 6px;
  font-size: 13px;
  color: #ef4444;
`;

interface LoginFormProps {
  email: string;
  password: string;
}

export const SignIn: FC = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormProps>({ mode: 'onTouched' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isGoogleLoggingIn, setIsGoogleLoggingIn] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { showError } = useHeroToast();
  const { signInWithEmail, signInWithGoogle, currentUser, isResolved } = useUserState();
  const { language, setLanguage } = useLanguageState();
  const navigate = useLocaleNavigate();

  // Monit „brak dostępu do panelu" po ścieżce, która wylogowała gościa poza
  // formularzem (np. reload z przywróconą sesją gościa — patrz userState).
  useLayoutEffect(() => {
    try {
      if (sessionStorage.getItem(PANEL_ACCESS_DENIED_KEY)) {
        sessionStorage.removeItem(PANEL_ACCESS_DENIED_KEY);
        showError(t(
          'auth.errors.panelNoAccess',
          'To konto nie ma dostępu do panelu. Zaloguj się kontem personelu hotelu.',
        ));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize language from browser on first mount
  useLayoutEffect(() => {
    const stored = localStorage.getItem('language-storage');
    if (!stored) {
      const browserLang = getBrowserLanguage();
      setLanguage(browserLang);
      i18n.changeLanguage(browserLang);
    } else if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect if already logged in (only after auth state is resolved)
  if (isResolved && currentUser) {
    return <LocaleNavigate to="/" replace />;
  }

  const getFirebaseErrorMessage = (error: unknown): string => {
    // Konto bez dostępu do panelu (np. gość aplikacji gościa) — Firebase Auth
    // je zalogował, ale personelem nie jest. Pokazujemy jasny monit.
    if (error instanceof PanelAccessError || (error as { code?: string })?.code === 'panel/no-access') {
      return t(
        'auth.errors.panelNoAccess',
        'To konto nie ma dostępu do panelu. Zaloguj się kontem personelu hotelu.',
      );
    }
    if (error instanceof FirebaseError) {
      const errorKey = `auth.errors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }
    return t('auth.errors.default');
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    setIsGoogleLoggingIn(true);
    // Pre-grzanie funkcji wołanej zaraz po wejściu do panelu — grzeje się w trakcie
    // round-tripu logowania, więc strzał przy ładowaniu panelu trafia w ciepłą instancję.
    warmupFunction('v3-getOrganizationsByIds');
    try {
      const user = await signInWithGoogle(rememberMe);

      // Check if email is verified (for consistency with email login)
      await user.reload();

      // Check if this is first login
      const createdAt = new Date(user.metadata.creationTime || 0).getTime();
      const lastSignIn = new Date(user.metadata.lastSignInTime || 0).getTime();
      const isFirstLogin = Math.abs(lastSignIn - createdAt) < 5000; // Within 5 seconds

      // For backwards compatibility: only check verification for new users
      const implementationDate = new Date('2026-02-22').getTime();
      const shouldCheckVerification = createdAt >= implementationDate;

      if (shouldCheckVerification && !user.emailVerified) {
        // Redirect to verify-email if email is not verified
        navigate('/verify-email', { state: { email: user.email } });
        return;
      }

      // Don't setIsGoogleLoggingIn(false) - keep showing loader during redirect
      navigate('/');
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
      setIsGoogleLoggingIn(false);
    }
  };

  const onSubmit = async (formData: LoginFormProps): Promise<void> => {
    setIsLoggingIn(true);
    // Pre-grzanie funkcji wołanej zaraz po wejściu do panelu (patrz handleGoogleSignIn).
    warmupFunction('v3-getOrganizationsByIds');
    try {
      const user = await signInWithEmail(formData.email, formData.password, rememberMe);

      // Check if email is verified
      await user.reload();

      // Check if this is first login (createdAt ~= lastSignInTime)
      const createdAt = new Date(user.metadata.creationTime || 0).getTime();
      const lastSignIn = new Date(user.metadata.lastSignInTime || 0).getTime();
      const isFirstLogin = Math.abs(lastSignIn - createdAt) < 5000; // Within 5 seconds

      // For backwards compatibility: only check verification for new users (created after implementation)
      // Users created before 2026-02-22 can log in without verification
      const implementationDate = new Date('2026-02-22').getTime();
      const shouldCheckVerification = createdAt >= implementationDate;

      if (shouldCheckVerification && !user.emailVerified) {
        // Redirect to verify-email if email is not verified
        // Keep user logged in so polling can work
        // Don't setIsLoggingIn(false) - keep showing loader during redirect
        navigate('/verify-email', { state: { email: formData.email } });
        return;
      }

      // Don't setIsLoggingIn(false) - keep showing loader during redirect
      navigate('/');
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
      setIsLoggingIn(false);
    }
  };

  return (
    <AuthContainer>
      <FormContainer>
        <FormWrapper>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>

          <WelcomeTitle>{t('auth.signIn.welcomeTitle')}</WelcomeTitle>
          <WelcomeSubtitle>
            {t('auth.signIn.welcomeSubtitle')}
          </WelcomeSubtitle>

          <AuthForm onSubmit={handleSubmit(onSubmit)} noValidate>
            <InputGroup>
              <InputLabel>{t('auth.signIn.emailLabel')}</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Mail />
                </InputIcon>
                <StyledInput
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.signIn.emailPlaceholder')}
                  {...register('email', {
                    required: t('auth.signIn.emailRequired'),
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: t('auth.signIn.emailInvalid'),
                    },
                  })}
                />
              </InputWrapper>
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </InputGroup>

            <InputGroup>
              <InputLabel>{t('auth.signIn.passwordLabel')}</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Lock />
                </InputIcon>
                <StyledInput
                  type={isPasswordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('auth.signIn.passwordPlaceholder')}
                  {...register('password', { required: t('auth.signIn.passwordRequired') })}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? <EyeOff /> : <Eye />}
                </PasswordToggle>
              </InputWrapper>
              {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            </InputGroup>

            <RememberRow>
              <CheckboxWrapper>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                {t('auth.signIn.rememberMe')}
              </CheckboxWrapper>
              <LocaleLink to="/forgot-password">
                <ForgotPasswordLink as="span">
                  {t('auth.signIn.forgotPassword')}
                </ForgotPasswordLink>
              </LocaleLink>
            </RememberRow>

            <SubmitButton type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? (
                <>
                  <Spinner
                    size="sm"
                    classNames={{
                      circle1: 'border-b-white',
                      circle2: 'border-b-white',
                    }}
                  />
                  <span>{t('auth.signIn.signingIn')}</span>
                </>
              ) : (
                t('auth.signIn.signInButton')
              )}
            </SubmitButton>
          </AuthForm>

          <Divider>
            <span>{t('auth.signIn.orContinueWith')}</span>
          </Divider>

          <SocialButton type="button" onClick={handleGoogleSignIn} disabled={isGoogleLoggingIn}>
            {isGoogleLoggingIn ? (
              <Spinner size="sm" classNames={{ circle1: 'border-b-gray-600', circle2: 'border-b-gray-600' }} />
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            {t('auth.signIn.continueWithGoogle')}
          </SocialButton>

          <LegalDisclaimer>
            <Trans
              i18nKey="auth.signIn.legalDisclaimer"
              components={{
                terms: <a href="/pl/regulamin" target="_blank" rel="noopener noreferrer" />,
                privacy: <a href="/privacy" target="_blank" rel="noopener noreferrer" />,
                cookies: <a href="/pl/cookies" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </LegalDisclaimer>

        </FormWrapper>
      </FormContainer>

      <MarketingSection>
        <MarketingContent>
          <MarketingTitle>
            {t('auth.signIn.marketingTitle')}
          </MarketingTitle>

          {/* Treść sekcji jedzie pionowym pokazem slajdów: cztery tematy zamiast jednej
              listy haseł, z numerowaną listwą postępu po lewej. */}
          <MarketingSlideshow />

          <PartnersSection>
            <PartnersTitle>{t('auth.signIn.trustedBy')}</PartnersTitle>
            <PartnersGrid>
              <img src={logoAccor} alt="Accor" />
              <img src={logoNovotel} alt="Novotel" />
              <img src={logoMercure} alt="Mercure" />
              {/* Znaki ibis są kwadratowe, więc przy tej samej wysokości co logotypy
                  słowne wychodziłyby optycznie cięższe. Klasa `mark` daje im niższy kadr. */}
              <img className="mark" src={logoIbis} alt="ibis" />
              <img className="mark" src={logoIbisStyles} alt="ibis Styles" />
              <img className="mark" src={logoIbisBudget} alt="ibis budget" />
              <img src={logoNickel} alt="Nickel" />
              <img src={logoForestPark} alt="Forest Park" />
              <img src={logoEssendi} alt="Essendi" />
            </PartnersGrid>
          </PartnersSection>
        </MarketingContent>
      </MarketingSection>
    </AuthContainer>
  );
};
